import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./RefDataDetails.css";
import { MdArrowBack } from "react-icons/md";
import * as Tooltip from "@radix-ui/react-tooltip";
import JsonToTable from "../../../Components/JsonToTable";
import AxiosService from "../../../Utils/AxiosService";

const formatEntityType = (entityType) => {
  if (!entityType) return "";

  return entityType
    .replace(/_cv$/, "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const RefDataDetails = () => {
  const { datasetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [datasetDetails, setDatasetDetails] = useState(null);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [items, setItems] = useState([]);
  const [entityTypesList, setEntityTypesList] = useState([]);
  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const apiService = useMemo(() => new AxiosService(), []);

  useEffect(() => {
    const fetchDatasetDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const classificationId =
          location.state?.classificationId || datasetId;
        const entityType = location.state?.entityType || [];
        const territory = location.state?.territory || "";
        const searchTerm = location.state?.searchTerm || "";
        const refDataValues = location.state?.refDataValues;
        const dataset = location.state?.dataset;

        setSelectedDataset(dataset);
        setEntityTypesList(entityType || []);

        const detailsResponse =
          await apiService.fetchRefDataValuesByClassification(
            classificationId,
            entityType,
            territory,
            searchTerm
          );

        const details = detailsResponse.data;

        setDatasetDetails(details);

        let refValues = [];
        let dataSource = null;

        // Determine which data source to use - check for nested refDataValues first
        if (refDataValues?.refDataValues && typeof refDataValues.refDataValues === "object") {
          dataSource = refDataValues.refDataValues;
        } else if (
          refDataValues &&
          typeof refDataValues === "object" &&
          !Array.isArray(refDataValues) &&
          !refDataValues.refDataValues
        ) {
          dataSource = refDataValues;
        } else if (details?.refDataValues && typeof details.refDataValues === "object") {
          dataSource = details.refDataValues;
        }

        // Convert to array format
        if (dataSource && typeof dataSource === "object" && !Array.isArray(dataSource)) {
          refValues = Object.entries(dataSource).map(([urn, value]) => ({
            urn,
            value,
          }));
        } else if (Array.isArray(dataSource)) {
          refValues = dataSource;
        }

        setItems(refValues);
        // Don't pre-select entity types - let user select them
        // setSelectedEntityTypes(entityType || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load dataset details");
      } finally {
        setLoading(false);
      }
    };

    if (datasetId) {
      fetchDatasetDetails();
    }
  }, [datasetId, location, apiService]);

  const handleEntityTypeToggle = async (entityType) => {
    try {
      const updatedEntityTypes = selectedEntityTypes.includes(entityType)
        ? selectedEntityTypes.filter((e) => e !== entityType)
        : [...selectedEntityTypes, entityType];

      setSelectedEntityTypes(updatedEntityTypes);
      setLoading(true);

      const classificationId =
        location.state?.classificationId || datasetId;
      const territory = location.state?.territory || "";
      const searchTerm = location.state?.searchTerm || "";

      // Call API with selected entity types as filter
      const response = await apiService.fetchRefDataValuesByClassification(
        classificationId,
        updatedEntityTypes,
        territory,
        searchTerm
      );

      const details = response.data;
      let refValues = [];
      let dataSource = null;

      if (details?.refDataValues && typeof details.refDataValues === "object") {
        dataSource = details.refDataValues;
      }

      if (dataSource && typeof dataSource === "object" && !Array.isArray(dataSource)) {
        refValues = Object.entries(dataSource).map(([urn, value]) => ({
          urn,
          value,
        }));
      } else if (Array.isArray(dataSource)) {
        refValues = dataSource;
      }

      setItems(refValues);
      setLoading(false);
    } catch (err) {
      console.error("Error filtering data:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedEntityTypes, searchValue]);

  // Items are already filtered from the API based on selected entity types
  const filteredItems = items.filter((item) =>
    item.value.toLowerCase().includes(searchValue.toLowerCase())
  );
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const handleItemClick = async (item) => {
    try {
      const urnParts = item.urn?.split(":");
      const entityTypeFromUrn = urnParts?.[3] || "";
      const matchingEntityType = entityTypesList.find((type) =>
        item.urn
          .toLowerCase()
          .includes(type.toLowerCase().replace("_cv", ""))
      ) || entityTypeFromUrn;

      const response = await apiService.completeCVDetail(
        matchingEntityType,
        item.urn
      );

      // Open modal with response data
      setModalData({
        title: item.value,
        data: response.data,
      });
      setModalOpen(true);

      console.log("Complete CV Detail Response:", response.data);
    } catch (err) {
      console.error("Error fetching CV details:", err);
    }
  };

  if (loading) {
    return (
      <div className="refdata-details-container">
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="refdata-details-container">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  return (
    <div className="refdata-details-container">
      <div className="details-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <MdArrowBack /> Back
        </button>

        <h1 className="details-title">{selectedDataset?.title}</h1>
      </div>

      <div className="items-section">
        {/* Sidebar */}

        <div className="items-sidebar">
          <h3 className="sidebar-title">Entity Types</h3>

          <div className="entity-types-list">
            {entityTypesList.map((entityType) => (
              <label key={entityType} className="entity-type-checkbox">
                <input
                  type="checkbox"
                  checked={selectedEntityTypes.includes(entityType)}
                  onChange={() => handleEntityTypeToggle(entityType)}
                />

                <span className="checkbox-label">{formatEntityType(entityType)}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Content */}

        <div className="items-content">
          <h2 className="items-title">
            Items in {selectedDataset?.title}
          </h2>

          <div className="search-container">
            <input
              type="text"
              placeholder="Search by Label..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="items-list">
            {paginatedItems.length > 0 ? (
              paginatedItems.map((item, index) => (
                <div
                  key={item.urn || index}
                  className="item-card"
                  style={{ "--index": startIndex + index }}
                  onClick={() => handleItemClick(item)}
                >
                  <div className="item-value">
                    <p className="value-text">{item.value}</p>
                  </div>
                  <div className="item-urn">
                    <p className="urn-text">{item.urn}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="no-data-message">No data found.</p>
            )}
          </div>

          {filteredItems.length > 0 && (
            <div className="pagination-container">
              <button
                className="pagination-btn"
                onClick={handlePrevious}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>

              <div className="page-numbers-container">
                {Array.from({ length: totalPages }, (_, i) => {
                  const pageNum = i + 1;
                  const isCurrentPage = pageNum === currentPage;

                  // Show first page, last page, current page, and pages around current
                  const showPage =
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    Math.abs(pageNum - currentPage) <= 1;

                  if (!showPage && (pageNum === 2 || pageNum === totalPages - 1)) {
                    return <span key="ellipsis" className="pagination-ellipsis">...</span>;
                  }

                  if (showPage) {
                    return (
                      <button
                        key={pageNum}
                        className={`page-btn ${isCurrentPage ? "active" : ""}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  return null;
                })}
              </div>

              <div className="pagination-info">
                <span className="item-count">
                  ({filteredItems.length} total items)
                </span>
              </div>

              <button
                className="pagination-btn"
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalOpen && modalData && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modalData.title}</h2>
              <button
                className="modal-close-btn"
                onClick={() => setModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <Tooltip.Provider delayDuration={200}>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <div className="modal-json-trigger">
                      {typeof modalData.data === "object"
                        ? JSON.stringify(modalData.data, null, 2)
                        : modalData.data}
                    </div>
                  </Tooltip.Trigger>
                  {/* <Tooltip.Portal>
                    <Tooltip.Content
                      side="top"
                      sideOffset={8}
                      avoidCollisions={true}
                      align="start"
                      alignOffset={5}
                      className="bg-light text-white border border-gray-300 rounded-lg shadow-lg p-2 text-xs max-w-4xl z-100 overflow-y-auto whitespace-normal break-words"
                    >
                      <JsonToTable
                        data={[modalData.data]}
                        defLang="en"
                      />
                      <Tooltip.Arrow className="fill-gray-100" />
                    </Tooltip.Content>
                  </Tooltip.Portal> */}
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefDataDetails;
